import Vue from 'vue';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Icon, Popover, formatDate, FieldType, arrayToCsv, downloadCsv} from 'wc-lib';
import { NemRegions } from '../config';
import * as screenfull from 'screenfull';
import { interpolateHtmlColor } from '../../../helpers/color';
import { CumulativePriceTable } from './cumulative-price-table';
import { IntervalRecord, mapApiDataToIntervals, setForecastIntervals } from './cumulative-price-common';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./cumulative-price.html'),
  name: 'cumulative-price-threshold',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'cumulative-price-table': CumulativePriceTable,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class CumulativePrice extends Vue {

  nemRegion: string = 'NEM';

  intervalRefresh: any;
  
  created() {
    this.regions = [...NemRegions, { value: 'NEM', label: this.nemRegion }];

    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }

    if (this.$route.query.Elec_location) {
      this.disabledRegions = (this.$route.query.Elec_location as string).split(',');
    }

    this.fetchData();
    this.intervalRefresh = setInterval(() => {
      this.fetchData();
    }, 10000);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }

  //
  // data
  //

  id: string;
  dynamicView: CustomDynamicView<any>;
  cumulativePriceThreshold: number = null;
  nemRegionData: NemRegion[] = [];
  nemChartData: any[] = [];
  chartData: Map<string, any[]> = null;
  tableData: any[] = [];

  async fetchData() {
    if (!this.cumulativePriceThreshold) {
      let id = await DataManager.addEndPoint('../api/report/NEM_DASHBOARD_MARKET_PRICE_LIMITS', undefined, 60000, 'NEM_DASHBOARD_MARKET_PRICE_LIMITS', undefined, 'yyyy-MM-ddTHH:mm:ss', []);   
      let view  = DataManager.getDynamicView(id, 'marketPriceLimits');
      this.cumulativePriceThreshold = view.data().find(x => x.KEY === "CumulativePriceThreshold").VALUE;
    }

    this.id = await DataManager.addEndPoint('../api/report/NEM_DASHBOARD_CUMUL_PRICE', undefined, 60000, 'NEM_DASHBOARD_CUMUL_PRICE', undefined, 'yyyy-MM-ddTHH:mm:ss', ['DT']);   
    this.dynamicView = DataManager.getDynamicView(this.id, 'cumulativePrice');

    let apiData: any[] = this.dynamicView.data();
    let intervalData: IntervalRecord[] = setForecastIntervals(mapApiDataToIntervals(apiData));

    this.nemRegionData = this.getNemRegionData(intervalData);
    this.nemChartData = this.getNemChartData(this.nemRegionData);
    this.chartData = this.getChartData(intervalData);
    this.tableData = this.getTableData(intervalData);

    this.createChart();
  }

  getNemChartData(nemRegionData: NemRegion[]): any[] {
    let nemChartData: any[] = [];

    for (let region of nemRegionData) {
      let negative: number = 0;
      let negativeTop: number = 0;
      let low: number = 0;
      let lowTop: number = 0;
      let medium: number = 0;
      let mediumTop: number = 0;
      let high: number = 0;
      let highTop: number = 0;
      let lineColor: string;
      switch (this.getCptLevel(region.cumulativePrice)) {
        case CptLevel.Negative:
          negative = region.cumulativePrice;
          negativeTop = this.cumulativePriceThreshold;
          lineColor = this.nemChartNegativeColor;
        break;
        case CptLevel.Low:
          low = region.cumulativePrice;
          lowTop = this.cumulativePriceThreshold - region.cumulativePrice;
          lineColor = this.nemChartLowColor;
        break;
        case CptLevel.Medium: 
          medium = region.cumulativePrice;
          mediumTop = this.cumulativePriceThreshold - region.cumulativePrice;
          lineColor = this.nemChartMediumColor;
        break;
        case CptLevel.High: 
          high = this.cumulativePriceThreshold;
          highTop = region.cumulativePrice - this.cumulativePriceThreshold;
          lineColor = this.nemChartHighColor;
        break;
      }
      nemChartData.push({ label: region.label, negative, negativeTop, low, lowTop, medium, mediumTop, high, highTop, lineColor });
    }

    return nemChartData;
  }

  getNemRegionData(intervalData: IntervalRecord[]): NemRegion[] {
    let nemRegionData: NemRegion[] = [];

    for (let region of NemRegions) {
      let regionData = intervalData.filter(d => d.regionId === region.value);
      let cumulativePrice: number = this.getCurrentInterval(regionData).cumulativePrice;
      nemRegionData.push({ label: region.label, cumulativePrice, cptLevelClass: this.getCptLevelClass(this.getCptLevel(cumulativePrice)) });
    }

    return nemRegionData;
  }

  getChartData(intervalData: IntervalRecord[]): Map<string, any[]> {
    let chartData: Map<string, any[]> = new Map<string, any[]>();

    for (let region of NemRegions) {
      chartData.set(region.value, intervalData.filter(d => d.regionId === region.value));
    }

    return chartData;
  }
 
  getTableData(intervalData: IntervalRecord[]): any[] {
    let map = new Map<string, any>();
    for (let row of intervalData) {
      let key: string = formatDate(row.dateTime, 'yyyy-MM-dd HH:mm:ss');
      let entry: any = map.get(key);
      if (entry === undefined) {
        entry = { dateTime: row.dateTime, CPT: this.cumulativePriceThreshold, isActual: row.isActual };
        map.set(key, entry);
      }
      entry[row.regionId] = row.cumulativePrice;
    }

    let tableData: any[] = [];
    for (let key of Array.from(map.keys()).sort()) {
      tableData.push(map.get(key));
    }

    return tableData;
  }

  getCurrentInterval(intervalData: IntervalRecord[]): IntervalRecord {
    let index = this.getCurrentIntervalIndex(intervalData);
    return index ? intervalData[index] : undefined;
  }

  getCurrentIntervalIndex(intervalData: IntervalRecord[]): number {
    for (let i = intervalData.length - 1; i >= 0; i--) {
      if (intervalData[i].isActual) {
        return i;
      }
    }

    return undefined;
  }

  //
  // chart
  //

  guideColor = '#777da7';
  nemChartBackgroundColor: string = "#F3F7F9";
  nemChartNegativeColor: string = "#3379BF";
  nemChartLowColor: string = "#48C0BB";
  nemChartMediumColor: string = "#FFD460";
  nemChartHighColor: string = "#FE5F55";
  selectedPeriod: string = '30MIN';

  data: any[] = [];
  currentInterval: any = null;
  nextInterval: any = null;
  selectedView: string = 'chart';
  chartConfig: any  = null;
  currentCptLevelClass: string = null;
  forecastCptLevelClass: string = null;


  createChart(): void {
    if (this.selectedRegion === this.nemRegion) {
      this.data = this.nemChartData;
      this.chartConfig = this.getNemChartConfig();
      return;
    }

    this.data = this.chartData.get(this.selectedRegion);

    // current and next interval
    this.currentInterval = null;
    this.nextInterval = null;
    let i = this.getCurrentIntervalIndex(this.data);
    if (i) {
      this.currentInterval = this.data[i];
      if ((i + 1) < this.data.length) {
        this.nextInterval = this.data[i+1];
      }
    }

    this.currentCptLevelClass = this.getCptLevelClass(this.getCptLevel(this.currentInterval.cumulativePrice));
    this.forecastCptLevelClass = this.getCptLevelClass(this.getCptLevel(this.nextInterval.cumulativePrice));

    this.addChartData();
    this.chartConfig = this.getChartConfig();
  }

  addChartData(): void {
    for (let row of this.data) {
      // CPT horizonal line
      row.CPT = this.cumulativePriceThreshold;

      // dashed forecast cumulative price
      if (row === this.currentInterval) {
        row.dashLength = 6;
      }
    }
  }

  getCptLevel(price: number): CptLevel {
    let ratio: number = price / this.cumulativePriceThreshold;
    if (ratio >= 1) {
      return CptLevel.High;
    }

    if (ratio >= 0.75) {
      return CptLevel.Medium;
    }

    if (ratio >= 0) {
      return CptLevel.Low;
    }

    return CptLevel.Negative;
  }

  getCptLevelClass(cptLevel: CptLevel): string {
    return 'cumulative-price-threshold-' + cptLevel.toLowerCase();
  }

  getCptLevelColor(cptLevel: CptLevel): string {
    switch (cptLevel)
    {
      case CptLevel.High: return this.nemChartHighColor;
      case CptLevel.Medium: return this.nemChartMediumColor;
      case CptLevel.Low: return this.nemChartLowColor;
      default: return this.nemChartNegativeColor;
    }
  }

  get isThresholdExceeded(): boolean {
    return this.currentInterval.cumulativePrice >= this.cumulativePriceThreshold;
  }

  chartRendered(chart: any) {
    this.addChartLabels(chart);
    chart.addListener('drawn', (e: any) => {
      e.chart.clearLabels();
      this.addChartLabels(e.chart);
    });
  }

  addChartLabels(chart: any) {
    if (this.selectedPeriod !== '5MIN' && chart.categoryAxis.dateToCoordinate && this.currentInterval.dateTime) {
      let x = chart.categoryAxis.dateToCoordinate(this.currentInterval.dateTime);
      if (isNaN(x)) return;

      let forecastX = x ? x + 120 : x;
      this.addChartLabel(chart, forecastX, 26, true, 'FORECAST', 'left', 10);

      let currentTimeX = x ? x + 90 : x;
      this.addChartLabel(chart, currentTimeX, 60, false, 'CURRENT (SPOT) TIME', 'right', 8);
      this.addChartLabel(chart, currentTimeX, 46, false, formatDate(this.currentInterval.dateTime, "HH:mm dd MMM"), 'right', 12);
    }
  }

  addChartLabel(chart: any, x: number, y: number, yFromTop: boolean, text: string, textAlign: string, textSize: number) {
    // chart.addLabel documented at https://docs.amcharts.com/3/javascriptcharts/AmChart#addLabel

    let rotation = 0;
    let alpha = 1;
    let bold = true;
    chart.addLabel(x, yFromTop ? y : '!' + y, text, textAlign, textSize, this.guideColor, rotation, alpha, bold);
  }

  getNemChartConfig() {
    // chart properties documented at https://docs.amcharts.com/3/javascriptcharts/AmGraph
    return {
      type: "serial",
      theme: "none",
      valueAxes: [{
        stackType: "regular",
        axisAlpha: 0,
        gridAlpha: 0,
        color: '#8ea9a9',
        title: 'CUMULATIVE PRICE ($)',
        titleColor: '#2c0831'
      }],
      graphs: [{
        balloonText: "",
        fillAlphas: 1,
        fillColors: [this.nemChartNegativeColor, this.nemChartBackgroundColor],
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "negative"
      }, {
        balloonText: "",
        fillAlphas: 0,
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "negativeTop"
      }, {
        balloonText: "",
        fillAlphas: 1,
        fillColors: [this.nemChartBackgroundColor, this.nemChartLowColor],
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "low"
      }, {
        balloonText: "",
        fillAlphas: 0,
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "lowTop"
      }, {
        balloonText: "",
        fillAlphas: 1,
        fillColors: [this.nemChartBackgroundColor, this.nemChartMediumColor],
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "medium"
      }, {
        balloonText: "",
        fillAlphas: 0,
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "mediumTop"
      }, {
        balloonText: "",
        fillAlphas: 1,
        fillColors: [this.nemChartBackgroundColor, interpolateHtmlColor(this.nemChartBackgroundColor, this.nemChartHighColor, 0.5)],
        lineAlpha: 1,
        lineColorField: "lineColor",
        lineThickness: 2,
        type: "column",
        valueField: "high"
      }, {
        balloonText: "",
        fillAlphas: 1,
        fillColors: [interpolateHtmlColor(this.nemChartBackgroundColor, this.nemChartHighColor, 0.5), this.nemChartHighColor],
        lineAlpha: 0,
        type: "column",
        valueField: "highTop"
      }],
      categoryField: "label",
      categoryAxis: {
        gridPosition: "start",
        axisAlpha: 0,
        gridAlpha: 0,
        position: "left"
      }
    };
  }

  getChartConfig() {
    const guides = [];
    if (this.selectedPeriod === '30MIN') {
      guides.push({
        date: this.currentInterval.dateTime,
        color: this.guideColor,
        lineColor: this.guideColor,
        lineAlpha: 1,
        fillAlpha: 0.1,
        dashLength: 3,
        lineThickness: 2,
        inside: false,
        above: true
      });
    }

    let cptLevelColor = this.getCptLevelColor(this.getCptLevel(this.currentInterval.cumulativePrice));
    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 10,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "left",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'CUMULATIVE PRICE ($)',
        titleColor: '#2c0831'
      }],
      graphs: [ 
        { 
          valueAxis: "v1", 
          balloonText: 'CUMULATIVE PRICE $[[value]]', 
          lineColor: cptLevelColor,
          valueField: 'cumulativePrice',
          lineThickness: 3, 
          dashLengthField: "dashLength",
          fillAlphas: 0.3,
          fillColors: [cptLevelColor, 'white'],
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          }
        },
        { 
          valueAxis: "v1",
          balloonText: 'CPT $[[value]]',
          dashLength: 3,
          lineColor: this.isThresholdExceeded ? this.getCptLevelColor(CptLevel.High) : this.guideColor,
          valueField: 'CPT',
          lineThickness: 2
        }
      ],
      
      categoryAxis: {
        parseDates: true,
        minPeriod: "mm",
        color: '#8ea9a9',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "dateTime",
      guides: guides,
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function(value) {
          var valueIndex = this.xToIndex(this.dateToCoordinate(value)),
              value = this.data[valueIndex],
              actual = value.dataContext.isActual,
              label = '';
          
          if (valueIndex == 0 ) {
            label = formatDate(value.dataContext.dateTime, "HH:mm dd MMM");
          } else {
            label = formatDate(this.data[valueIndex-1].dataContext.dateTime, "HH:mm-") + 
            formatDate(value.dataContext.dateTime, "HH:mm dd MMM");
          } 
          
          label += actual ? ' Actual' : ' Forecast';
          
          return label;
        }
      }
    };
  }
  
  get currentDateTime(): string {
    return formatDate(this.currentInterval.dateTime, 'HH:mm dd MMM');
  }

  //
  // region
  //
  
  regions = [];
  disabledRegions: string[] = [];
  selectedRegion: string = 'NSW1';

  get isRegionDisabled(): boolean {
    return this.disabledRegions.includes(this.selectedRegion);
  }

  selectRegion(region: string): void {
    this.selectedRegion = region;
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {
    this.createChart();
  }

  //
  // table
  //

  tableColumns = [
    {
      title: 'Settlement Date',
      field: 'dateTime',
      formatter: (value) => {
        let date = value.dateTime || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Cumulative Price (NSW)',
      field: 'NSW1',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Cumulative Price (QLD)',
      field: 'QLD1',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Cumulative Price (VIC)',
      field: 'VIC1',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Cumulative Price (SA)',
      field: 'SA1',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Cumulative Price (TAS)',
      field: 'TAS1',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Cumulative Price Threshold',
      field: 'CPT',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Type',
      field: 'type',
      sortable: true
    }
  ];

  download() {
    let rows = this.tableData.map( r => ({ ...r, type: r.isActual ? 'Actual' : 'Forecast'})); // for some reason the formatter tableColumns formatter doesn't work, so do it manually
    let columns = this.tableColumns;
    let csv = arrayToCsv(rows, columns);
    downloadCsv(`NEMCUMULATIVEPRICE_ALL_${formatDate(this.currentInterval.dateTime, 'yyyyMMddHHmm')}.csv`, csv);
  }

  //
  // full screen
  //

  isFullscreen = false;

  toggleFullscreen() {
    (screenfull as any).toggle();
  }

}

enum CptLevel { 
  Negative = "Negative",
  Low = "Low", 
  Medium = "Medium", 
  High = "High" 
}

interface NemRegion {
  label: string;
  cumulativePrice: number;
  cptLevelClass: string;
}


// WEBPACK FOOTER //
// src/views/electricity/nem/cumulative-price.ts